'use client';

import { useEffect } from 'react';
import { useRive, useViewModelInstanceNumber } from '@rive-app/react-canvas';
import { ProgressBar } from 'types/navigation';
import styles from './TopNavBar/TopNavBar.module.scss';

const P3_PROGRESS_RIVE_SRC = '/mah-progressbar_(p3).riv';

/** Stable options so `useRive` does not treat each parent render as a new load. */
const P3_RIVE_OPTIONS = {
  src: P3_PROGRESS_RIVE_SRC,
  autoBind: true,
  stateMachines: 'State Machine 1',
  autoplay: true,
} as const;

/**
 * P3 Rive has two inputs: `startProgressLine` (first segment) and
 * `finalProgressLine` (second segment). Redux still uses `ProgressBar.third`
 * for legacy; compliance steps set it to 0 and only `first` / `second` apply.
 */
const ComplianceRiveProgressCanvas: React.FC<{
  startProgressLine: number;
  finalProgressLine: number;
}> = ({ startProgressLine, finalProgressLine }) => {
  const { rive, RiveComponent } = useRive(P3_RIVE_OPTIONS);

  const progressBarInstance = rive?.viewModelInstance;

  const { setValue: setStartProgressLine } = useViewModelInstanceNumber(
    'startProgressLine',
    progressBarInstance
  );

  const { setValue: setFinalProgressLine } = useViewModelInstanceNumber(
    'finalProgressLine',
    progressBarInstance
  );

  useEffect(() => {
    if (setStartProgressLine) {
      setStartProgressLine(startProgressLine || 0);
    }
    if (setFinalProgressLine) {
      setFinalProgressLine(finalProgressLine || 0);
    }
  }, [
    startProgressLine,
    finalProgressLine,
    setStartProgressLine,
    setFinalProgressLine,
  ]);

  return (
    <div className={styles.timelineWrapper}>
      <RiveComponent />
    </div>
  );
};

/** P3 compliance progress bar: different `.riv` and view-model property names than legacy. */
const ComplianceRiveProgressBar: React.FC<ProgressBar> = ({
  first,
  second,
  label,
  step,
}) => {
  const stepTextMobile = step === 0 ? label : `Step ${step} of 8: ${label}`;

  return (
    <div>
      <span className={styles.progressBarText}>{label}</span>
      <span className={styles.progressBarTextMobile}>{stepTextMobile}</span>
      <ComplianceRiveProgressCanvas
        startProgressLine={first}
        finalProgressLine={second}
      />
    </div>
  );
};

export default ComplianceRiveProgressBar;
